import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { X, Trash2, Send } from 'lucide-react';

const CommentsModal = ({ task, onClose, currentUser }) => {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadComments = () => {
    axios.get(`/api/tasks/${task.id}/comments`)
      .then(r => { setComments(r.data.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadComments(); }, [task.id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [comments]);

  const sendComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`/api/tasks/${task.id}/comments`, { content: text.trim() });
      setText('');
      loadComments();
    } catch (err) { alert(err.response?.data?.message || 'Failed to send'); }
    finally { setSending(false); }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try { await axios.delete(`/api/tasks/comments/${commentId}`); loadComments(); }
    catch { alert('Failed to delete comment'); }
  };

  const initials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  const canDelete = (c) => c.userId === currentUser?.id || ['admin', 'manager'].includes(currentUser?.role);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{task.title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {task.project?.name && <span>{task.project.name} � </span>}Comments
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          {task.description && (
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {task.description}
            </div>
          )}
          <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 16 }}>
            {loading && <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>Loading...</div>}
            {!loading && !comments.length && (
              <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 24 }}>No comments yet � be the first!</div>
            )}
            {comments.map(c => (
              <div key={c.id} className="comment-item">
                <div className="comment-avatar">{initials(c.author?.name)}</div>
                <div className="comment-body">
                  <div className="comment-meta">
                    <span className="comment-author">{c.author?.name}</span>
                    <span className="comment-time">
                      {new Date(c.createdAt).toLocaleDateString()} {new Date(c.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {canDelete(c) && (
                      <button onClick={() => deleteComment(c.id)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 2px', marginLeft: 'auto', opacity: 0.7 }}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  <div className="comment-text">{c.content}</div>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={sendComment} style={{ display: 'flex', gap: 8 }}>
            <input className="input" style={{ flex: 1 }} placeholder="Write a comment..."
              value={text} onChange={e => setText(e.target.value)} disabled={sending} />
            <button type="submit" className="btn btn-primary" disabled={sending || !text.trim()} style={{ padding: '10px 14px', flexShrink: 0 }}>
              <Send size={15} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommentsModal;
